/**
 * LightsIndirect.js - Indirect Lighting (Global Illumination)
 *
 * Pure TSL: Fn(), If(), Loop(), .toVar(), .assign() — NO wgslFn()
 *
 * Material-only bounce direction sampling. Environment is NOT an indirect
 * strategy — it is handled via deterministic NEE in LightsSampling.js.
 *
 * Contains:
 *  - calculateTransmissionPDF  — transmission PDF for MIS
 *  - calculateClearcoatPDF     — clearcoat PDF for MIS
 *  - computeSamplingInfo       — compute importance weights for material strategies
 *  - selectSamplingStrategy    — CDF-based strategy selection (specular, diffuse, transmission, clearcoat)
 *  - calculateIndirectLighting — material multi-strategy MIS for bounce direction
 */

import {
	Fn,
	float,
	vec2,
	vec3,
	int,
	bool as tslBool,
	max,
	abs,
	sqrt,
	dot,
	If,
	select,
} from 'three/tsl';

import {
	IndirectLightingResult,
} from './LightsCore.js';
import {
	SamplingStrategyWeights,
} from './Struct.js';
import {
	PI_INV,
	EPSILON,
	MIN_PDF,
} from './Common.js';
import { DistributionGGX, calculateVNDFPDF, specularVNDFPDFForDir } from './MaterialProperties.js';
import { evaluateMaterialResponse } from './MaterialEvaluation.js';
import { RandomValue } from './Random.js';
import { sampleMicrofacetTransmission, MicrofacetTransmissionResult } from './MaterialTransmission.js';
import { cosineWeightedSample } from './MaterialSampling.js';

// =============================================================================
// PDF Calculation Helpers
// =============================================================================

// Transmission PDF (Walter et al. 2007)
export const calculateTransmissionPDF = Fn( ( [ V, L, N, ior, roughness, entering ] ) => {

	// eta is the relative IOR: eta_transmitted / eta_incident
	// When entering: air(1.0) -> material(ior), so eta = ior
	// When exiting: material(ior) -> air(1.0), so eta = 1.0/ior
	const eta = select( entering, ior, float( 1.0 ).div( ior ) ).toVar();

	// Transmission half-vector formula
	const H_raw = V.add( L.mul( eta ) ).toVar();
	const lenSq = dot( H_raw, H_raw ).toVar();
	const H = select( lenSq.greaterThan( EPSILON ), H_raw.div( sqrt( lenSq ) ), N ).toVar();

	// Ensure H points into the correct hemisphere
	If( dot( H, N ).lessThan( 0.0 ), () => {

		H.assign( H.negate() );

	} );

	const VoH = abs( dot( V, H ) );
	const LoH = abs( dot( L, H ) ).toVar();
	const NoH = abs( dot( N, H ) ).toVar();

	// GGX distribution
	const D = DistributionGGX( NoH, roughness );

	// Jacobian for transmission
	const denom_inner = VoH.add( LoH.mul( eta ) ).toVar();
	const denom = denom_inner.mul( denom_inner );
	const jacobian = LoH.mul( eta ).mul( eta ).div( max( denom, EPSILON ) );

	return D.mul( NoH ).mul( jacobian );

} );

// Clearcoat PDF
export const calculateClearcoatPDF = Fn( ( [ V, L, N, clearcoatRoughness ] ) => {

	const H_raw = V.add( L ).toVar();
	const lenSq = dot( H_raw, H_raw ).toVar();
	const H = select( lenSq.greaterThan( EPSILON ), H_raw.div( sqrt( lenSq ) ), N );

	const NoH = max( dot( N, H ), 0.0 );
	const NoV = max( dot( N, V ), 0.0 );

	return calculateVNDFPDF( NoH, NoV, clearcoatRoughness );

} );

// =============================================================================
// Sampling Strategy Computation
// =============================================================================

// Compute normalized strategy weights based on importance info
export const computeSamplingInfo = Fn( ( [
	samplingInfo,
] ) => {

	// Material-only strategies (env handled via deterministic NEE in direct lighting)
	const specularW = samplingInfo.specularImportance.toVar();
	const useSpecular = specularW.greaterThan( 0.001 ).toVar();

	const diffuseW = samplingInfo.diffuseImportance.toVar();
	const useDiffuse = diffuseW.greaterThan( 0.001 ).toVar();

	const transmissionW = samplingInfo.transmissionImportance.toVar();
	const useTransmission = transmissionW.greaterThan( 0.001 ).toVar();

	const clearcoatW = samplingInfo.clearcoatImportance.toVar();
	const useClearcoat = clearcoatW.greaterThan( 0.001 ).toVar();

	const totalW = specularW.add( diffuseW ).add( transmissionW ).add( clearcoatW ).toVar();

	If( totalW.lessThan( 0.001 ), () => {

		specularW.assign( 0.0 );
		diffuseW.assign( 1.0 );
		transmissionW.assign( 0.0 );
		clearcoatW.assign( 0.0 );
		totalW.assign( 1.0 );
		useSpecular.assign( tslBool( false ) );
		useDiffuse.assign( tslBool( true ) );
		useTransmission.assign( tslBool( false ) );
		useClearcoat.assign( tslBool( false ) );

	} ).Else( () => {

		const invTotal = float( 1.0 ).div( totalW ).toVar();
		specularW.mulAssign( invTotal );
		diffuseW.mulAssign( invTotal );
		transmissionW.mulAssign( invTotal );
		clearcoatW.mulAssign( invTotal );
		totalW.assign( 1.0 );

	} );

	return SamplingStrategyWeights( {
		envWeight: float( 0.0 ),
		specularWeight: specularW,
		diffuseWeight: diffuseW,
		transmissionWeight: transmissionW,
		clearcoatWeight: clearcoatW,
		totalWeight: totalW,
		useEnv: tslBool( false ),
		useSpecular,
		useDiffuse,
		useTransmission,
		useClearcoat,
	} );

} );

// =============================================================================
// Strategy Selection via Cumulative Distribution
// =============================================================================

// Strategy IDs: 1=specular, 2=diffuse, 3=transmission, 4=clearcoat
// (env removed — handled via deterministic NEE in direct lighting)
export const selectSamplingStrategy = Fn( ( [ weights, randomValue ] ) => {

	const selectedStrategy = int( 2 ).toVar(); // Default: diffuse

	const cumulative = float( 0.0 ).toVar();
	const found = tslBool( false ).toVar();

	If( weights.useSpecular.and( found.not() ), () => {

		cumulative.addAssign( weights.specularWeight );

		If( randomValue.lessThan( cumulative ), () => {

			selectedStrategy.assign( 1 );
			found.assign( tslBool( true ) );

		} );

	} );

	If( weights.useDiffuse.and( found.not() ), () => {

		cumulative.addAssign( weights.diffuseWeight );

		If( randomValue.lessThan( cumulative ), () => {

			selectedStrategy.assign( 2 );
			found.assign( tslBool( true ) );

		} );

	} );

	If( weights.useTransmission.and( found.not() ), () => {

		cumulative.addAssign( weights.transmissionWeight );

		If( randomValue.lessThan( cumulative ), () => {

			selectedStrategy.assign( 3 );
			found.assign( tslBool( true ) );

		} );

	} );

	If( weights.useClearcoat.and( found.not() ), () => {

		selectedStrategy.assign( 4 );

	} );

	return selectedStrategy;

} );

// =============================================================================
// Cosine Weighted PDF helper
// =============================================================================

const cosineWeightedPDF = Fn( ( [ NoL ] ) => {

	return max( NoL, 0.0 ).mul( PI_INV );

} );

// =============================================================================
// Indirect Lighting Calculation
// =============================================================================

export const calculateIndirectLighting = Fn( ( [
	V, N, material,
	// brdfSample fields (DirectionSample)
	brdfSampleDirection, brdfSamplePdf, brdfSampleValue,
	rngState,
	samplingInfo,
] ) => {

	// Initialize result
	const r_direction = vec3( 0.0 ).toVar();
	const r_throughput = vec3( 0.0 ).toVar();
	const r_misWeight = float( 0.0 ).toVar();
	const r_pdf = float( 0.0 ).toVar();
	const r_combinedPdf = float( 0.0 ).toVar();

	// Validate input sampling info
	const validInput = samplingInfo.diffuseImportance.greaterThanEqual( 0.0 )
		.and( samplingInfo.specularImportance.greaterThanEqual( 0.0 ) )
		.and( samplingInfo.transmissionImportance.greaterThanEqual( 0.0 ) )
		.and( samplingInfo.clearcoatImportance.greaterThanEqual( 0.0 ) );

	If( validInput.not(), () => {

		// Fallback to diffuse sampling
		const r1_fb = RandomValue( rngState ).toVar();
		const r2_fb = RandomValue( rngState ).toVar();
		const sampleRand = vec2( r1_fb, r2_fb );
		r_direction.assign( cosineWeightedSample( N, sampleRand ) );
		r_throughput.assign( material.color.xyz );
		r_misWeight.assign( 1.0 );
		r_pdf.assign( 1.0 );

	} ).Else( () => {

		// Use corrected sampling info
		const weights = SamplingStrategyWeights.wrap( computeSamplingInfo(
			samplingInfo,
		).toVar() );

		const selectionRand = RandomValue( rngState ).toVar();
		const r1 = RandomValue( rngState ).toVar();
		const r2 = RandomValue( rngState ).toVar();
		const sampleRand = vec2( r1, r2 ).toVar();

		// Strategy selection
		const selectedStrategy = selectSamplingStrategy( weights, selectionRand ).toVar();

		const sampleDir = vec3( 0.0 ).toVar();
		const samplePdf = float( 0.0 ).toVar();
		const sampleBrdfValue = vec3( 0.0 ).toVar();
		// Transmission lobe uses this weight instead of a reflection-BRDF eval, which is
		// invalid for the below-surface refraction direction.
		const transColorWeight = vec3( 1.0 ).toVar();

		// Execute selected strategy (chained If/ElseIf/Else for exclusive branches)
		// Environment removed — handled via deterministic NEE in direct lighting

		// Strategy 1: Specular
		If( selectedStrategy.equal( int( 1 ) ), () => {

			sampleDir.assign( brdfSampleDirection );
			samplePdf.assign( brdfSamplePdf );
			sampleBrdfValue.assign( brdfSampleValue );

		} ).ElseIf( selectedStrategy.equal( int( 2 ) ), () => {

			// Strategy 2: Diffuse
			sampleDir.assign( cosineWeightedSample( N, sampleRand ) );
			samplePdf.assign( cosineWeightedPDF( max( dot( N, sampleDir ), 0.0 ) ) );
			sampleBrdfValue.assign( evaluateMaterialResponse( V, sampleDir, N, material ) );

		} ).ElseIf( selectedStrategy.equal( int( 3 ) ), () => {

			// Strategy 3: Transmission
			const entering = dot( V, N ).greaterThan( 0.0 );
			const mtResult = MicrofacetTransmissionResult.wrap( sampleMicrofacetTransmission(
				V, N, material.ior, material.roughness, entering, material.dispersion, sampleRand, rngState, float( 0.0 )
			).toVar() );
			sampleDir.assign( mtResult.direction );
			samplePdf.assign( mtResult.pdf );
			// evaluateMaterialResponse is reflection-only and returns a non-physical value for
			// the below-surface refraction direction (its dots are floored to 0.001). Carry the
			// sampler's spectral tint and apply an energy-consistent transmission throughput
			// below (mirrors handleTransmission's material.color × colorWeight convention).
			transColorWeight.assign( mtResult.colorWeight );

		} ).Else( () => {

			// Strategy 4: Clearcoat (fallback)
			sampleDir.assign( brdfSampleDirection );
			samplePdf.assign( brdfSamplePdf );
			sampleBrdfValue.assign( brdfSampleValue );

		} );

		const NoL = max( dot( N, sampleDir ), 0.0 ).toVar();

		// Calculate combined PDF for MIS (material strategies only)
		const combinedPdf = float( 0.0 ).toVar();

		If( weights.useSpecular, () => {

			// Evaluate specular PDF at sampleDir (not brdfSampleDirection which may differ);
			// auto-selects iso/aniso to stay consistent with the BRDF sampler.
			const specPdfAtSampleDir = specularVNDFPDFForDir( material, N, V, sampleDir );
			combinedPdf.addAssign( weights.specularWeight.mul( specPdfAtSampleDir ) );

		} );

		If( weights.useDiffuse, () => {

			combinedPdf.addAssign( weights.diffuseWeight.mul( cosineWeightedPDF( NoL ) ) );

		} );

		If( weights.useTransmission.and( material.transmission.greaterThan( 0.0 ) ), () => {

			// Calculate transmission PDF for this direction
			const entering = dot( V, N ).greaterThan( 0.0 );
			combinedPdf.addAssign( weights.transmissionWeight.mul(
				calculateTransmissionPDF( V, sampleDir, N, material.ior, material.roughness, entering )
			) );

		} );

		If( weights.useClearcoat.and( material.clearcoat.greaterThan( 0.0 ) ), () => {

			// Calculate clearcoat PDF for this direction
			combinedPdf.addAssign( weights.clearcoatWeight.mul(
				calculateClearcoatPDF( V, sampleDir, N, material.clearcoatRoughness )
			) );

		} );

		// Ensure valid PDFs
		samplePdf.assign( max( samplePdf, MIN_PDF ) );
		combinedPdf.assign( max( combinedPdf, MIN_PDF ) );

		// MIS weight calculation
		const misWeight = samplePdf.div( combinedPdf ).toVar();

		// Reflection lobes: f·NoL·mis/pdf. Transmission lobe: the sampler's energy-consistent
		// weight (material tint × dispersion colorWeight × mis) — the reflection BRDF value is
		// invalid below the surface, so it is not used for transmission.
		const reflThroughput = sampleBrdfValue.mul( NoL ).mul( misWeight ).div( samplePdf );
		const transThroughput = material.color.xyz.mul( transColorWeight ).mul( misWeight );

		r_direction.assign( sampleDir );
		r_throughput.assign( select( selectedStrategy.equal( int( 3 ) ), transThroughput, reflThroughput ) );
		r_misWeight.assign( misWeight );
		r_pdf.assign( samplePdf );
		r_combinedPdf.assign( combinedPdf );

	} ); // End validInput check

	return IndirectLightingResult( {
		direction: r_direction,
		throughput: r_throughput,
		misWeight: r_misWeight,
		pdf: r_pdf,
		combinedPdf: r_combinedPdf,
	} );

} );
